import React from "react";

interface MetricItem {
  label: string;
  value: string;
  change?: string;
  changeDirection?: "up" | "down" | "neutral";
}

export interface SubNavItem {
  id: string;
  label: string;
  shortcutId: string;
  hasData?: boolean;
}

interface ScoreGaugePanelProps {
  score: number;
  scoreChange: number;
  period: string;
  subtitle: string;
  metrics: MetricItem[];
  badge?: string;
  title?: string;
  centerLabel?: string;
  subNavItems?: SubNavItem[];
  activeSubNav?: string;
  onSubNavClick?: (item: SubNavItem) => void;
}

// Accent falls back to Search violet until a card sets --accent on an ancestor.
const ACCENT = "var(--accent, #8B5CF6)";
const GAUGE_R = 64;
const GAUGE_CIRC = 2 * Math.PI * GAUGE_R;

const ScoreGaugePanel: React.FC<ScoreGaugePanelProps> = ({
  score,
  scoreChange,
  period,
  subtitle,
  metrics,
  badge = "Search Intelligence",
  title = "Search Intelligence Score",
  centerLabel = "OVERALL",
  subNavItems = [],
  activeSubNav,
  onSubNavClick,
}) => {
  const clampedScore = Math.max(0, Math.min(100, score));
  const dashOffset = GAUGE_CIRC * (1 - clampedScore / 100);

  return (
    <div className="relative overflow-hidden rounded-2xl border border-white/[0.07] bg-og01-card p-6 md:p-7 mb-5">
      {/* Corner accent wash */}
      <div
        className="pointer-events-none absolute -left-[4%] -top-[30%] h-[340px] w-[340px]"
        style={{ background: `radial-gradient(circle, color-mix(in srgb, ${ACCENT} 16%, transparent), transparent 65%)` }}
      />

      <div className="relative">
        {/* Badge row */}
        <div className="mb-5 flex items-center gap-3">
          <span
            className="rounded-full px-3 py-1.5 text-[13px] font-semibold"
            style={{
              color: ACCENT,
              background: `color-mix(in srgb, ${ACCENT} 12%, transparent)`,
              border: `1px solid color-mix(in srgb, ${ACCENT} 26%, transparent)`,
            }}
          >
            {badge}
          </span>
          <span className="text-[13px] text-og01-text-muted">{title}</span>
        </div>

        {/* Gauge + right column */}
        <div className="grid grid-cols-1 items-center gap-8 md:grid-cols-[auto_1fr]">
          {/* Gauge */}
          <div className="relative mx-auto h-[150px] w-[150px] flex-none">
            <svg width="150" height="150" viewBox="0 0 150 150" className="-rotate-90">
              <circle cx="75" cy="75" r={GAUGE_R} fill="none" stroke="rgba(255,255,255,.06)" strokeWidth="11" />
              <circle
                cx="75"
                cy="75"
                r={GAUGE_R}
                fill="none"
                stroke={ACCENT}
                strokeWidth="11"
                strokeLinecap="round"
                strokeDasharray={GAUGE_CIRC}
                strokeDashoffset={dashOffset}
                style={{ transition: "stroke-dashoffset 1s ease-out" }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="font-heading text-[50px] leading-none text-og01-text">{score}</div>
              <div className="mt-1 text-[10px] uppercase tracking-[0.16em] text-og01-text-muted">{centerLabel}</div>
            </div>
            {scoreChange !== 0 && (
              <div
                className="absolute top-2 left-1/2 translate-x-[30px] text-[13px] font-semibold"
                style={{ color: scoreChange >= 0 ? "#22C55E" : "#EF4444" }}
              >
                {scoreChange >= 0 ? `+${scoreChange}` : scoreChange}
              </div>
            )}
          </div>

          {/* Right column */}
          <div className="min-w-0">
            <h3 className="mb-1 font-heading text-[26px] leading-[1.1] text-og01-text">{title}</h3>
            <p className="mb-5 text-[13.5px] text-og01-text-secondary">{subtitle}</p>
            <span
              className="mb-4 inline-block rounded-full px-[11px] py-[5px] text-xs text-og01-text-muted"
              style={{ background: `color-mix(in srgb, ${ACCENT} 10%, transparent)` }}
            >
              {period}
            </span>
            <div className="grid grid-cols-2 gap-5 md:grid-cols-4">
              {metrics.map((metric, i) => (
                <div key={i}>
                  <div className="mb-1.5 text-[10.5px] uppercase tracking-[0.12em] text-og01-text-muted">{metric.label}</div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-heading text-[28px] leading-none text-og01-text">{metric.value}</span>
                    {metric.change && (
                      <span
                        className="text-xs font-medium"
                        style={{ color: metric.changeDirection === "up" ? "#22C55E" : metric.changeDirection === "down" ? "#EF4444" : "#8080A0" }}
                      >
                        {metric.change}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sub-nav (tools — each chip = a shortcutId; green dot = saved data) */}
        {subNavItems.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-2.5 border-t border-white/[0.07] pt-[22px]">
            {subNavItems.map((item) => {
              const isActive = activeSubNav === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => onSubNavClick?.(item)}
                  className="inline-flex items-center gap-2 rounded-[10px] px-4 py-2.5 text-[13px] font-semibold transition-colors"
                  style={
                    isActive
                      ? { background: ACCENT, border: "1px solid transparent", color: "#fff" }
                      : { background: "#0F0F18", border: "1px solid rgba(255,255,255,.07)", color: "#ABABC8" }
                  }
                >
                  {item.label}
                  {item.hasData && !isActive && (
                    <span
                      className="inline-block h-[7px] w-[7px] rounded-full"
                      style={{ background: "#22C55E", boxShadow: "0 0 0 3px color-mix(in srgb, #22C55E 22%, transparent)" }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScoreGaugePanel;
